import { useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import * as Dialog from '@radix-ui/react-dialog'
import { ChevronLeft } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { Container, ContainerContent } from '@/components/ui/Container'
import { toast } from '@/hooks/use-toast'
import { SalesOrderSettingsForm } from '@/features/sales-order/settings/SalesOrderSettingsForm'
import {
  hasADateFormat,
  settingsEqual,
  toUpdateRequest,
} from '@/features/sales-order/settings/salesOrderSettingsMappers'
import {
  customerSalesOrderSettingsQueryKey,
  useCustomerSalesOrderSettings,
  useWorkflowSalesOrderSettings,
} from '@/features/sales-order/salesOrderSettingsQueries'
import {
  deleteCustomerSalesOrderSettings,
  saveCustomerSalesOrderSettings,
} from '@/services/api'
import type { SalesOrderSettings } from '@/types/salesOrderSettings'

/**
 * One customer's settings, which override the workflow's for that customer's orders.
 *
 * A customer that has none is sent here with ?create=1, and the draft starts as a copy of the
 * workflow's settings as they stand right now — so creating an override and saving it changes
 * nothing until something in it is changed.
 *
 * The customer's name rides along in the URL. The customer list is paginated, so there is no one
 * query to read a name out of the way the order confirmation side reads it from its supplier list.
 */
export function CustomerSettingsPage() {
  const { workflowId, customerId } = useParams<{ workflowId: string; customerId: string }>()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const isCreateMode = searchParams.get('create') === '1'
  const customerName = searchParams.get('name') ?? 'Customer'

  const [savedSettings, setSavedSettings] = useState<SalesOrderSettings | null>(null)
  const [draftSettings, setDraftSettings] = useState<SalesOrderSettings | null>(null)
  const [showRemoveConfirm, setShowRemoveConfirm] = useState(false)

  const {
    data: customerSettings,
    isLoading: customerSettingsLoading,
    isError: customerSettingsError,
    error: customerSettingsLoadError,
  } = useCustomerSalesOrderSettings(workflowId, customerId, !isCreateMode)

  const {
    data: globalSettings,
    isLoading: globalSettingsLoading,
    isError: globalSettingsError,
    error: globalSettingsLoadError,
  } = useWorkflowSalesOrderSettings(workflowId)

  useEffect(() => {
    if (isCreateMode) {
      if (!globalSettings) return
      setSavedSettings(null)
      setDraftSettings({ ...globalSettings, id: '' })
      return
    }

    if (customerSettingsLoading) return

    if (customerSettings) {
      setSavedSettings(customerSettings)
      setDraftSettings(customerSettings)
      return
    }

    // The customer follows the workflow's settings. Creating an override is the only thing this
    // page can do for them, so it switches to doing that rather than showing an empty form.
    if (globalSettings) {
      navigate(
        `/sales-order/${workflowId}/customers/${customerId}/settings?create=1&name=${encodeURIComponent(customerName)}`,
        { replace: true },
      )
    }
  }, [
    customerName,
    customerId,
    customerSettings,
    customerSettingsLoading,
    globalSettings,
    isCreateMode,
    navigate,
    workflowId,
  ])

  const hasUnsavedChanges = isCreateMode
    ? !!draftSettings
    : savedSettings && draftSettings
      ? !settingsEqual(savedSettings, draftSettings)
      : false
  // The backend refuses a save with every date format off, so the button says so before the trip.
  const canSave = !!draftSettings && hasADateFormat(draftSettings)

  const saveMutation = useMutation({
    mutationFn: () => {
      if (!workflowId || !customerId || !draftSettings) {
        throw new Error('Settings are not loaded')
      }
      return saveCustomerSalesOrderSettings(workflowId, customerId, toUpdateRequest(draftSettings))
    },
    onSuccess: (updated) => {
      setSavedSettings(updated)
      setDraftSettings(updated)
      queryClient.setQueryData(customerSalesOrderSettingsQueryKey(workflowId!, customerId!), updated)
      void queryClient.invalidateQueries({ queryKey: ['sales-order-customers', workflowId] })
      void queryClient.invalidateQueries({ queryKey: ['customer-order-detail'] })
      toast({ title: isCreateMode ? 'Customer settings created' : 'Customer settings saved' })
      if (isCreateMode) {
        navigate(
          `/sales-order/${workflowId}/customers/${customerId}/settings?name=${encodeURIComponent(customerName)}`,
          { replace: true },
        )
      }
    },
    onError: (err: Error) => {
      toast({
        title: 'Could not save customer settings',
        description: err.message,
        variant: 'destructive',
      })
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => {
      if (!workflowId || !customerId) {
        throw new Error('Customer is not loaded')
      }
      return deleteCustomerSalesOrderSettings(workflowId, customerId)
    },
    onSuccess: () => {
      queryClient.removeQueries({
        queryKey: customerSalesOrderSettingsQueryKey(workflowId!, customerId!),
      })
      void queryClient.invalidateQueries({ queryKey: ['sales-order-customers', workflowId] })
      void queryClient.invalidateQueries({ queryKey: ['customer-order-detail'] })
      toast({ title: 'Customer settings removed' })
      navigate(`/sales-order/${workflowId}/customers`)
    },
    onError: (err: Error) => {
      toast({
        title: 'Could not remove customer settings',
        description: err.message,
        variant: 'destructive',
      })
    },
  })

  if (!workflowId || !customerId) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-500">
        No customer selected.
      </div>
    )
  }

  if (customerSettingsError || globalSettingsError) {
    const message =
      (customerSettingsLoadError instanceof Error && customerSettingsLoadError.message) ||
      (globalSettingsLoadError instanceof Error && globalSettingsLoadError.message) ||
      'Could not load customer settings'
    return (
      <div className="flex items-center justify-center py-16 text-sm text-red-600">{message}</div>
    )
  }

  const isLoading = isCreateMode
    ? globalSettingsLoading
    : customerSettingsLoading || globalSettingsLoading

  if (isLoading || !draftSettings) {
    return (
      <div className="flex items-center justify-center py-16 text-sm text-gray-400">Loading…</div>
    )
  }

  return (
    <div className="container mx-auto">
      <Container>
        <ContainerContent className="space-y-8 py-6">
          <header className="space-y-4">
            <Link
              to={`/sales-order/${workflowId}/customers`}
              className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-900"
            >
              <ChevronLeft className="h-4 w-4" />
              Back to customers
            </Link>
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">{customerName}</h1>
                <p className="mt-2 text-sm text-gray-600">
                  {isCreateMode
                    ? 'Create custom settings for this customer. They will override the global settings.'
                    : 'Custom settings for this customer override the global settings.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2">
                {!isCreateMode && (
                  <Button type="button" variant="outline" onClick={() => setShowRemoveConfirm(true)}>
                    Remove customer settings
                  </Button>
                )}
                <Button variant="cta"
                  type="button"
                  disabled={!hasUnsavedChanges || !canSave || saveMutation.isPending}
                  onClick={() => saveMutation.mutate()}
                >
                  {saveMutation.isPending ? 'Saving…' : isCreateMode ? 'Create' : 'Save'}
                </Button>
              </div>
            </div>
          </header>

          <SalesOrderSettingsForm settings={draftSettings} onChange={setDraftSettings} />
        </ContainerContent>
      </Container>

      <Dialog.Root open={showRemoveConfirm} onOpenChange={setShowRemoveConfirm}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/25 backdrop-blur-[1px]" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-panel bg-white p-6 shadow-lg">
            <Dialog.Title className="text-base font-semibold text-gray-900">
              Remove customer settings
            </Dialog.Title>
            <Dialog.Description className="mt-2 text-sm text-gray-500">
              Remove custom settings for {customerName}? This customer will use the global settings
              instead.
            </Dialog.Description>
            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowRemoveConfirm(false)}>
                Cancel
              </Button>
              <Button
                type="button"
                variant="destructive"
                disabled={removeMutation.isPending}
                onClick={() => removeMutation.mutate()}
              >
                {removeMutation.isPending ? 'Removing…' : 'Remove settings'}
              </Button>
            </div>
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}
